<template>
    <div class="fan-tokens">
        <div class="container fan-tokens__wrapper">
            <wallet-card v-for="(token, index) in tokensWithBalance"
                :id="`wallet-token-${index}`"
                :key="index"
                :token="token"
            />
        </div>

        <section class="summary">
            <div class="summary__wrapper container">
                <div class="summary__balance">
                    <p>{{ totalTokenValueMessage }}</p>
                    <div class="summary__text">
                        <span class="summary__text--bold">{{ totalTokenValue }} </span> {{ !isFiatDefaultCurrency ? totalTokenAlternativeValue : '' }}
                    </div>
                </div>
                <div class="summary__amount">
                    <p>{{ totalFanTokenMessage }}</p>
                    <div class="summary__text">
                        <span class="summary__text--bold">{{ totalFanTokenAmount }} </span>
                        <span class="summary__text--label">{{ $t('wallet.excludingSSU') }}</span>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>

<script>
// 3rd party libs
import {
    mapGetters,
} from 'vuex';

// Utils
import {
    formatPrice,
} from '@shared/utils/formatter';

// Socios variables
import socios from '@webapp/socios.json';

// Components
import walletCard from '@webapp/modules/crypto-and-trading/components/wallet/wallet-card.vue';

export default {
    name: 'wallet-fan-tokens',
    components: {
        walletCard,
    },
    data() {
        return {
            tokensWithBalance: '',
            partnerToken: {
                balance: 0,
                currency: {
                    shortCode: '',
                },
            },
        };
    },
    computed: {
        ...mapGetters('wallet', [
            'ledgersByDecentralizedByBalance',
            'userTokensPortfolio',
        ]),

        ...mapGetters([
            'defaultCurrency',
            'preferredFiatCurrency',
            'getCurrencyShortCodeFormatted',
            'isPartnerApp',
            'partnerAppData',
        ]),

        isFiatDefaultCurrency() {
            return this.defaultCurrency.type === 'fiat';
        },

        totalTokenValue() {
            return this.setTokenChzValue();
        },

        totalTokenValueMessage() {
            return (this.isPartnerApp
                ? this.$t('wallet.partnerTokenValue', {
                    partnerName: this.partnerToken.currency.shortCode,
                })
                : this.$t('wallet.totalTokenValue'));
        },

        totalTokenAlternativeValue() {
            return this.setTokenAlternativeValue();
        },

        totalFanTokenAmount() {
            return (this.isPartnerApp
                ? this.partnerToken.balance
                : Number(this.userTokensPortfolio.totalNumberOfFanToken).toFixed(0));
        },

        totalFanTokenMessage() {
            return (this.isPartnerApp
                ? this.$t('wallet.partnerFanTokens', {
                    partnerName: this.partnerToken.currency.shortCode,
                })
                : this.$t('wallet.totalToken'));
        },
    },
    created() {
        this.setTokenBalance();
    },
    methods: {
        setTokenBalance() {
            this.tokensWithBalance = this.ledgersByDecentralizedByBalance.filter((ledger) => (Number(ledger.balance) + Number(ledger.lockedBalance)) > 0 &&
                ledger.currencyNetwork.currency.currentMainNetwork === ledger.currencyNetwork.network.type);

            if (this.isPartnerApp) {
                this.tokensWithBalance = this.tokensWithBalance.filter((ledger) => (ledger.currency.shortCode === 'SSU') ||
                    (ledger.currency.shortCode === this.partnerAppData.shortName));

                if (this.tokensWithBalance.filter((ledger) => (ledger.currency.shortCode === this.partnerAppData.shortName)).length) {
                    this.partnerToken = this.tokensWithBalance.find((ledger) => ledger.currency.shortCode === this.partnerAppData.shortName);
                } else {
                    this.partnerToken.currency.shortCode = this.partnerAppData.shortName;
                }
            }
        },

        setTokenChzValue() {
            let total = Number(this.userTokensPortfolio.totalAmountOfFanToken);

            if (this.isPartnerApp) {
                if (this.partnerToken.balance > 0) {
                    total = Number(this.partnerToken.convertedPrices[socios.chilizCurrencyId]?.balance || 0) +
                    Number(this.partnerToken.convertedPrices[socios.chilizCurrencyId]?.lockedBalance || 0);
                } else {
                    return formatPrice(this.partnerToken.balance, this.getCurrencyShortCodeFormatted(this.defaultCurrency.id), !this.isFiatDefaultCurrency);
                }
            }

            return total > 0
                ? formatPrice(total, this.getCurrencyShortCodeFormatted(this.defaultCurrency.id), !this.isFiatDefaultCurrency)
                : '';
        },

        setTokenAlternativeValue() {
            let total = this.userTokensPortfolio.totalAmountOfFanTokenConverted;

            if (this.isPartnerApp) {
                if (this.partnerToken.balance > 0) {
                    total = Number(this.partnerToken.convertedPrices[this.preferredFiatCurrency.id]?.balance || 0) +
                    Number(this.partnerToken.convertedPrices[this.preferredFiatCurrency.id]?.lockedBalance || 0);
                } else {
                    total = this.partnerToken.balance;
                }
            }

            if (total <= 0) {
                return '';
            }

            return `≈${formatPrice(total, this.getCurrencyShortCodeFormatted(this.preferredFiatCurrency.id), true)}`;
        },
    },
};
</script>

<style lang="scss" scoped>
@import "@webapp/modules/crypto-and-trading/styles/wallet/common.scss";

.fan-tokens {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    flex: 1;

    &__wrapper {
        width: 100%;
    }
}

.summary {
    background: url("~@webapp/assets/images/wallet/summary-bg.webp") no-repeat center;
    background-size: cover;
    font-size: 1.5rem;
    color: $white-color;
    margin-top: 3.7rem;
    position: absolute;
    bottom: 0;
    z-index: 2;
    width: 100%;

    @include max-width(lg) {
        font-size: 1.1rem;
    }

    p {
        margin: 0 0 0 0.5rem;
    }

    &__wrapper {
        display: flex;
        justify-content: space-between;
        padding: 2rem 0;

        @include max-width(lg) {
            padding: 1.2rem 1.7rem;
        }
    }

    &__amount {
        text-align: right;
    }

    &__text {
        @include max-width(lg) {
            display: flex;
            flex-direction: column;
            font-weight: 700;
        }

        &--bold {
            font-size: 3.6rem;
            font-weight: 700;

            @include max-width(lg) {
                font-size: 2.1rem;
            }
        }

        &--label {

            @include max-width(lg) {
                font-weight: normal;
            }
        }
    }
}
</style>